/**
 * سجل القدرات — مصدر السلطة في النظام.
 *
 * لا يوجد في هذا المشروع سطر واحد يقارن باسم دور. السلطة قدرات مسمّاة، والأدوار
 * حزم منح عليها. البلوبرنت §3.
 *
 * هذا السجل يبدأ بقدرات المنصّة التي تحتاجها المرحلة الأولى، **وينمو موديولاً
 * بموديول** — لا تُضاف قدرة إلا مع اعتماد الشجرة الفرعية لموديولها.
 */
/**
 * أنواع النطاق **الثمانية** — البلوبرنت §3.
 *
 * و«عميلٌ يديره» نزل في المرحلة الثامنة بقرار شادي: **شاتُ العميل واجتماعاتُه
 * لمدير حسابه والإدارة وحدهما، لا لفريقه**. وهي علاقةٌ أضيق من العضوية، تُشتقّ
 * من وسم مدير الحساب — وله فهرسٌ فريدٌ جزئيّ في القاعدة.
 */
export const SCOPE_TYPES = [
    "company",
    "department",
    "assigned-client",
    "managed-client",
    "creator",
    "assignee",
    "own-record",
    "explicit-record",
];
export const capabilities = {
    // ===== المنصّة =====
    "platform.health.view": {
        module: "platform",
        label: { ar: "عرض صحة النظام", en: "View system health" },
        description: {
            ar: "حالة كل خدمة، والحوادث، وعمق الطوابير، والمهام المجدولة.",
            en: "Every service's status, incidents, queue depth, and scheduled jobs.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    "platform.incidents.resolve": {
        module: "platform",
        label: { ar: "إغلاق الحوادث", en: "Resolve incidents" },
        description: {
            ar: "وسم حادثة بأنها عولجت، وإخراجها من قائمة المفتوح.",
            en: "Mark an incident handled and clear it from the open list.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "platform.audit.view": {
        module: "platform",
        label: { ar: "عرض السجلّ الجنائي", en: "View the audit log" },
        description: {
            ar: "من فعل ماذا ومتى — بالبحث والتصفية بالفاعل والكيان والفترة.",
            en: "Who did what and when — searchable by actor, entity and period.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    /**
     * **ويُنضَّف السجلُّ بالمدّة — ولا يُمسح سطرٌ بعينه أبداً** (قرار شادي ٢٠٢٦-١٠-١٩).
     *
     * فمسحُ صفٍّ بعينه **تلاعبٌ لا تنظيف**: من يستطيع أن يمحو صفَّه هو يستطيع أن
     * يُنكر فعلَه. **ولا طريقَ لذلك في النظام أصلاً** — فما لا يوجد لا يُسمَح به
     * بالسهو، ولا يُطلَب يوماً من مديرٍ أن «يشيل سطراً».
     *
     * **والمسحُ نفسُه يُسجَّل** بسببه وبعدد ما حُذف، **وبتحقُّقٍ ثانٍ**: فهو في
     * قائمة الثمانية المعتمَدة («حذف من السجل الجنائي»).
     */
    "platform.audit.purge": {
        module: "platform",
        label: { ar: "تنظيف السجلّ القديم", en: "Purge the old audit log" },
        description: {
            ar: "تمسح سجلّ النشاط الأقدم من المدة المحددة في الإعدادات — بسبب مكتوب، ويُسجَّل المسح نفسه. ولا يُمسح سطرٌ واحد بعينه.",
            en: "Delete activity older than the retention window set in settings — with a written reason, and the purge itself is logged. A single row is never deletable.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **وسجلُّ التغييرات للموظفين وحدهم** (قرار شادي) — فالعميلُ يدخل ليوافق ويمضي.
     */
    "platform.changelog.manage": {
        module: "platform",
        label: { ar: "سجلّ التغييرات", en: "The changelog" },
        description: {
            ar: "تكتب «ما الجديد» لكل نسخة: مسودة، ثم مراجعة، ثم نشر — وتظهر النافذة للفريق مرة واحدة لكل نسخة.",
            en: "Write what is new in each release: draft, review, publish — and the team sees each release once.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **وتعديلُ الإشعار: نصُّه وقنواتُه — ولا جمهورُه ولا شدّتُه** (قرار شادي).
     *
     * **فالجمهورُ محسوبٌ من قدرة تصرّفٍ واعيةٍ بالنطاق**، وتغييرُه بيدٍ **ثقبُ
     * أمانٍ لا تفضيل**: به يُصنَع إشعارٌ يصل من لا يحقّ له أن يعرف. **والشدّةُ
     * تحدّد التصعيدَ والصوت**، وتنزيلُها يجعل حاجةً حرجةً تعدّي ساكتة.
     *
     * **وبتحقُّقٍ ثانٍ**: فنصُّ إشعارٍ يقرؤه كلُّ من في الوكالة، **ومن يملك
     * تغييرَه يملك أن يقول باسم النظام ما لم يقله**.
     */
    "platform.notifications.manage": {
        module: "platform",
        label: { ar: "إدارة الإشعارات", en: "Manage notifications" },
        description: {
            ar: "تغيّر صياغة أي إشعار باللغتين، وتوقف التنبيه على الهاتف أو البريد لنوع معيّن. أما من يصله فلا يتغيّر — فهو محسوب من الصلاحية.",
            en: "Reword any notification in both languages, and switch phone or email delivery off for a type. Who receives it never changes — that comes from the capability.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "platform.settings.manage": {
        module: "platform",
        label: { ar: "تعديل إعدادات النظام", en: "Change system settings" },
        description: {
            ar: "الهوية والألوان والحدود والسياسات ومصطلحات النظام.",
            en: "Identity, colours, limits, policies and system terminology.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    // ===== الهوية =====
    "identity.profile.manage_own": {
        module: "identity",
        label: { ar: "تعديل ملفي الشخصي", en: "Manage my profile" },
        description: {
            ar: "الصورة والاسم واللغة والمظهر وكلمة السر والأجهزة.",
            en: "Photo, name, language, theme, password and devices.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
    },
    "identity.sessions.revoke_own": {
        module: "identity",
        /**
         * **ورؤيتُها من إنهائها** — قدرةٌ واحدةٌ لسطحٍ واحد.
         *
         * فمن يجوز له أن يقفل جهازاً يجوز له أن يرى أجهزتَه، **ومن لا يرى لا
         * يقفل**: الزرُّ على صفٍّ لا يُقرأ لا يُضغَط. وقدرتان لشيءٍ واحد تعني
         * حالةً ثالثة — يرى ولا يقطع — لا يحتاجها أحد.
         */
        label: { ar: "أجهزتي وجلساتي", en: "My devices and sessions" },
        description: {
            ar: "رؤية الأجهزة التي فُتح عليها الحساب وإغلاق أيٍّ منها.",
            en: "See the devices the account is open on and end any of them.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
    },
    /**
     * **تنبيهُ جهازي — نظيرةُ `portal.push.manage` للموظّف.**
     *
     * وكانت القناةُ للعميل وحده: المُفرِّغُ يرسل للموظّف ولا زرَّ له يشترك به، **فلا
     * يصله تنبيهٌ واحد** وجدولُ القنوات يقول «تعمل». ورؤيتُها من تفعيلها، كأجهزتي.
     */
    "identity.push.manage_own": {
        module: "identity",
        label: { ar: "تنبيه جهازي", en: "Notify my devices" },
        description: {
            ar: "تفعيل التنبيه على هذا الجهاز وإيقافه عن أيّ جهاز، وإرسال تنبيه تجربة لنفسي.",
            en: "Turn notifications on for this device, off for any device, and send myself a test.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
    },
    // ===== الشات =====
    /**
     * **القناةُ العامة — الفريقُ كلُّه**، وواحدةٌ في النظام يفرضها فهرس.
     *
     * ووصولُها **بالقدرة لا بعضوية**: فصفوفُ عضويةٍ على قناةٍ للجميع تعني
     * مزامنةً عند كل مستخدمٍ جديد — وفي v2 كانت ثلاثَ دوالِّ مزامنةٍ تُنادى من
     * موديولات أخرى، **فكلُّ مسارٍ ينسى واحدةً يُنتج قناةً لا يراها من يحقّ له**.
     */
    "chat.general.view": {
        module: "chat",
        label: { ar: "القناة العامة", en: "The general channel" },
        description: {
            ar: "قناة الفريق كلّه — إعلانات وحديث عامّ. واحدة في النظام، ولا تُطوى.",
            en: "The whole team's channel — announcements and general talk. One in the system, and it never folds.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    /**
     * **وقناةُ العميل الداخلية** — أعضاؤها فريقُه، ووصولُها من عضويته.
     *
     * **وهي غيرُ قناة العميل نفسِه**: هذه كلامُ الوكالة عن شغله، وتلك كلامُه معنا.
     */
    "chat.client.view": {
        module: "chat",
        label: { ar: "قناة العميل الداخلية", en: "A client's internal channel" },
        description: {
            ar: "حديث الفريق عن عمل عميل — لا يراه العميل. قناة لكل عميل، ولا تُطوى.",
            en: "The team's talk about a client's work — the client never sees it. One per client, and it never folds.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: false,
    },
    "chat.direct.start": {
        module: "chat",
        label: { ar: "محادثة موظّف", en: "Message a colleague" },
        description: {
            ar: "تبدأ محادثة بينك وبين موظّف. محادثة واحدة لكل اثنين دائماً — فلو بدأتماها في اللحظة نفسها تنشأ واحدة.",
            en: "Start a one-to-one with a colleague. One per pair, always — if you both start it at once you still get one.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **والجروبُ يُصنَع بسهولة** (قرار شادي ٢٠٢٦-١٠-١٨).
     *
     * **ولا يُربَط بعميلٍ ولا بقسم** — فذلك يناقض «بسهولة». ووصولُه **عضويتُه**:
     * علاقةٌ معلَنة، فلا سياقَ فارغٌ يُقرأ شركةً كما كان في v2.
     */
    "chat.group.create": {
        module: "chat",
        label: { ar: "إنشاء مجموعة", en: "Create a group" },
        description: {
            ar: "تنشئ مجموعة مع عدة موظفين وتضمّ إليها آخرين. غير مربوطة بعميل ولا قسم — أعضاؤها وحدهم يرونها.",
            en: "Make a group with several colleagues and add people to it. Not tied to a client or a department — whoever is in it is who sees it.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "chat.send": {
        module: "chat",
        label: { ar: "الكتابة في المحادثات", en: "Write in chat" },
        description: {
            ar: "تكتب في أي قناة تصل إليها، وترفع فيها ملفات، وتشير إلى أشخاص وأقسام.",
            en: "Write in any channel you can reach, attach files, and mention people and departments.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **ورسالتُه يعدّلها ويمسحها، والأثرُ يبان** (قرار شادي).
     *
     * فرسالةٌ تختفي بلا أثر **تجعل النقاشَ غيرَ مفهوم**: ردٌّ معلّقٌ على كلامٍ لا
     * وجود له، ومن يقرأ بعد أسبوعٍ يظنّ أن أحداً يهرّج.
     */
    "chat.edit_own": {
        module: "chat",
        label: { ar: "تعديل رسالتي", en: "Edit my message" },
        description: {
            ar: "تعدّل رسالتك أو تحذفها — ويبقى سطر يقول «عُدِّلت» أو «رسالة محذوفة»، فيبقى النقاش مفهوماً.",
            en: "Edit or delete your own message — a line stays saying it was edited or removed, so the thread still reads.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
    },
    "chat.moderate": {
        module: "chat",
        label: { ar: "الإشراف على المحادثات", en: "Moderate chat" },
        description: {
            ar: "تحذف رسالة أي شخص وتطوي قناة. ويبقى أثر الحذف كالعادة.",
            en: "Delete anyone's message and fold a channel. A deletion still leaves its trace.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **وشاتُ العميل لمدير حسابه والإدارة وحدهما** (قرار شادي ٢٠٢٦-١٠-١٨).
     *
     * ونصُّه: «**فريق العميل لا، مش من حقه يشوف الشات ولا يرد عليه**». ولذلك نزل
     * نطاق «عميلٌ يديره»: **منحُها بنطاق العضوية يفتح شاتَ كلِّ عميلٍ هو عضوٌ
     * فيه** لا الذي يديره.
     */
    "chat.portal.respond": {
        module: "chat",
        label: { ar: "الردّ على محادثة العميل", en: "Reply to a client's chat" },
        description: {
            ar: "تقرأ كلام العميل في بوابة العميل وتردّ عليه — باسم الوكالة، لا باسمك. لمدير الحساب والإدارة فقط.",
            en: "Read the client's messages in their portal and reply — as the agency, not under your name. For the account manager and management only.",
        },
        allowedScopes: ["company", "managed-client"],
        actionCapability: true,
    },
    // ===== الاجتماعات =====
    /**
     * **والقراءةُ منفصلةٌ عن البتّ** — فمن يرى الطلبات ليس بالضرورة من يحسمها.
     *
     * وفي v2 كانت `meetings.manage` **واحدةً بنطاق الشركة تحرس تسعَ عمليات**،
     * فمديرُ الحساب **لا يستطيع الردَّ على عميله هو** ويلزم أن يمرّ كلُّ موعدٍ
     * بالإدارة. وهما قدرتان بقرار شادي: ويبتُّ مديرُ الحساب لعملائه، والإدارةُ
     * للكلّ — **بنطاق «عميلٌ يديره»**، وهو النوعُ الذي نزل للشات ويخدم الاثنين.
     */
    "meetings.view": {
        module: "meetings",
        label: { ar: "اجتماعات العميل", en: "A client's meetings" },
        description: {
            ar: "تعرض طلبات الاجتماع مع عميلك ومواعيدها وحالتها — من طلب، وما الذي تأكّد، وما الذي ينتظر ردّاً.",
            en: "See meeting requests with your client, their times and state — who asked, what is confirmed, and what is waiting on a reply.",
        },
        allowedScopes: ["company", "managed-client"],
        actionCapability: false,
    },
    /**
     * **والبتُّ أربعةُ أفعال في قدرةٍ واحدة** — فكلُّها «أحسم موعداً مع عميلي».
     *
     * **ولا يقبل طرفٌ اقتراحَه هو** — والفحصُ على **جهة الاقتراح** لا على هوية
     * الفاعل: فقد يقترح مديرُ الحساب ويقبل زميلُه، وذلك مقبول — **فالوكالةُ طرفٌ
     * واحد**. وهذا حكمٌ في آلة الحالة لا في القدرة.
     */
    "meetings.decide": {
        module: "meetings",
        label: { ar: "البتّ في الاجتماع", en: "Decide a meeting" },
        description: {
            ar: "توافق على الموعد، أو تقترح موعداً آخر، أو ترفض، أو تلغي — ثم تسجّل هل عُقد أم لم يحضر العميل. ويُضاف الرابط مع التأكيد.",
            en: "Approve the time, propose another, decline, or cancel — and afterwards say it happened or the client did not show. The link is set on confirmation.",
        },
        allowedScopes: ["company", "managed-client"],
        actionCapability: true,
    },
    /**
     * **خيطُ الاجتماع** (قرار شادي 2026-10-20: «كلام الاجتماعات وحضورها —
     * أيوه طبعاً اعمله»).
     *
     * **وv3 نزل بلا خيطٍ ولا حضور**: فاجتماعٌ فيه موضوعٌ وموعدٌ وقرار **ولا أحدَ
     * يعرف من يحضره ولا ما قيل في تجهيزه**. وv2 كان فيه الاثنان.
     *
     * **والحضورُ بلا مفتاحٍ جديد**: `meetings.decide` — فمن يوافق على الاجتماع
     * ويقترح موعده **هو من يقول من يحضره**.
     */
    "meetings.comment": {
        module: "meetings",
        label: { ar: "التعليق على اجتماع", en: "Comment on a meeting" },
        description: {
            ar: "تعليق داخلي على الاجتماع وإشارة — تجهيزه والمتابعة بعده، ولا يراه العميل. والمرفق مع التعليق.",
            en: "An internal comment and mentions on a meeting — preparation and follow-up, never seen by the client. Files travel with the comment.",
        },
        allowedScopes: ["company", "managed-client"],
        actionCapability: true,
    },
    "meetings.comments.moderate": {
        module: "meetings",
        label: { ar: "إدارة تعليقات الاجتماعات", en: "Moderate meeting comments" },
        description: {
            ar: "تعديل تعليق غيرك على اجتماع أو حذفه — ومعه ملفاته.",
            en: "Edit or delete someone else's comment on a meeting — and its files with it.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    // ===== التحليلات والقياس =====
    /**
     * **أرقامي أنا** — ويراها كلُّ موظّف، كما يرى شغلَه.
     *
     * فرقمُ أدائك ليس سرّاً عنك: من يُقاس يحقّ له أن يرى بماذا يُقاس، **وإلّا صار
     * الترتيبُ حكماً بلا بيان**.
     */
    "analytics.own.view": {
        module: "analytics",
        label: { ar: "أرقامي", en: "My numbers" },
        description: {
            ar: "أرقام أدائي في أي مدة — نسبة التزامي، والمتأخّر، ومتوسط ردّي على الإشارات. وأي رقم أضغط عليه يفتح القائمة التي حُسب منها.",
            en: "My own performance for any period — my on-time rate, what ran late, and how fast I reply to mentions. Any number opens the list it was counted from.",
        },
        allowedScopes: ["own-record"],
        actionCapability: false,
    },
    /**
     * **الترتيبُ كاملاً بالأسماء** (قرار شادي ٢٠٢٦-١٠-١٥) — فعدمُ الالتزام يبان.
     *
     * ويحمل الصفُّ **أرقامَ الترتيب وحدها**: الاسم والموضع ونسبةُ الالتزام وعددُ
     * المتأخّر. ولا اسمَ عميلٍ ولا عنوانَ عنصر — **فهو القائمةُ الوحيدة التي
     * يسافر فيها اسمٌ عبر الأقسام**، فيُبنى من قائمةٍ بيضاء كاستجابة البورتال.
     */
    "analytics.ranking.view": {
        module: "analytics",
        label: { ar: "ترتيب الموظفين", en: "The ranking" },
        description: {
            ar: "ترتيب الفريق كله بالأسماء حسب نسبة الالتزام — والمتأخّر يظهر في الأسفل. أرقام الترتيب فقط، بلا تفاصيل عمل ولا أسماء عملاء.",
            en: "The whole team ranked by name on their on-time rate — late work sinks you. Ranking figures only: no work detail and no client names.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    /**
     * **ولوحةُ القسم لرئيسه** (قرار شادي) — بنطاق «أقسامي».
     *
     * والفرقُ عن الترتيب ليس تناقضاً: الترتيبُ سؤالُ «مَن أين يقف»، **ولوحةُ القسم
     * سؤالُ «ما داخل هذا القسم»** — عملاؤه وعناصرُه وأحمالُه. ولو فُتحت لعرف رئيسُ
     * قسمٍ عملاءَ قسمٍ آخر وتفاصيلَ شغلهم.
     */
    "analytics.department.view": {
        module: "analytics",
        label: { ar: "أرقام قسمي", en: "My department's numbers" },
        description: {
            ar: "لوحة القسم كاملة: أرقام موظفيه، وأحمالهم، وعملاء القسم، وأي رقم يفتح قائمته.",
            en: "The department's full board: its people's numbers, their workload, its clients, and every number opens its list.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: false,
    },
    "analytics.company.view": {
        module: "analytics",
        label: { ar: "اللوحة التنفيذية", en: "The executive board" },
        description: {
            ar: "أرقام الشركة كلها مع مبدّل للمدة ومقارنة بالمدة السابقة، والأهداف وحالتها.",
            en: "The whole company's numbers with a period switch, a comparison against the period before, and the targets with their state.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    /**
     * **وصحّةُ العميل قدرةٌ بعينها** — فهي بياناتٌ عن عميلٍ لا عن شغلٍ.
     *
     * وفي v2 كانت تُفرَض على **التفصيل وحده**: البطاقةُ تُرسَم من حِمل اللوحة بلا
     * بوّابة، **فالصفُّ قابلٌ للنقر ويموت بـ`403`** — عيبٌ مسجَّلٌ بدرجة P2. وهنا
     * البوّابةُ واحدةٌ تحرس الرقمَ وتفصيلَه معاً.
     */
    "analytics.clients.view": {
        module: "analytics",
        label: { ar: "صحة العملاء", en: "Client health" },
        description: {
            ar: "أرقام كل عميل على حدة: التزامنا معه، وعدد العناصر المفتوحة لديه، وعدد مرات الرفض. وأي رقم يفتح عناصره.",
            en: "Each client's own numbers: how well we kept to their dates, how much is open, how often they rejected. Any number opens their items.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    /**
     * **والإدارةُ تحدّد الأهداف** (قرار شادي) — ومن يرى اللوحةَ يرى أهدافَها.
     *
     * ولا قدرةَ عرضٍ ثانية: **هدفٌ لا يراه صاحبُ اللوحة ليس هدفاً**. وكانت في v2
     * قدرتين، إحداهما لعرضٍ يحدث على نفس السطح.
     */
    "analytics.goals.manage": {
        module: "analytics",
        label: { ar: "تحديد أهداف الشركة", en: "Set the company targets" },
        description: {
            ar: "تحديد هدف لمقياس وشهر («لا يقلّ عن ٩٠٪»)، وتعديله وحذفه. ويصل إشعار فور تحقّقه أو الإخفاق فيه.",
            en: "Set a target for a metric and a month (“at least 90%”), change it, delete it. A notification goes out the moment it is met or missed.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **والتصديرُ إجراءٌ حرج** — كتصدير بيانات العميل.
     *
     * فالملفُّ يحمل أداءَ عملاءَ بأسمائهم **ويُنقَر إلى صفوفه**: خروجُ بياناتٍ لا
     * عرضُ شاشة. **وتصديرُ أرقامي أنا لا يطلبها** — فمن يصدّر أرقامَه لا يُخرِج
     * بياناتَ أحد، وكان v2 يفعل ذلك بالسهو لا بإعلان.
     */
    "analytics.export": {
        module: "analytics",
        label: { ar: "تصدير التحليلات", en: "Export analytics" },
        description: {
            ar: "تنزيل الأرقام والقوائم التي حُسبت منها كملف. إجراء حرج — يطلب تأكيد هويتك.",
            en: "Download the numbers and the lists behind them as a file. A critical action — it asks you to confirm it is you.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    // ===== التقارير =====
    "reports.view": {
        module: "reports",
        label: { ar: "عرض التقارير", en: "View reports" },
        description: {
            ar: "رؤية تقارير الأداء ومراحل مراجعتها.",
            en: "See performance reports and where they are in review.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator"],
        actionCapability: false,
    },
    "reports.create": {
        module: "reports",
        label: { ar: "كتابة تقرير", en: "Write a report" },
        description: {
            ar: "كتابة تقرير أداء لعميل ورفعه للمراجعة — تقرير واحد في الشهر، والثاني يطلب استثناءً.",
            en: "Write a client performance report and submit it — one a month, a second needs an exception.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    "reports.update": {
        module: "reports",
        label: { ar: "تعديل تقريري", en: "Edit my report" },
        description: {
            ar: "تعديل ما لم يُعتمَد بعد — والمنشور يُعدَّل بنسخة جديدة تمرّ بالمراجعتين.",
            en: "Edit what is not approved yet — a published one is edited as a new version that goes through both reviews.",
        },
        allowedScopes: ["company", "department", "creator"],
        actionCapability: true,
    },
    /**
     * **والكاتبُ لا يوافق على ما كتب** (قرار شادي ٢٠٢٦-١٠-١٥).
     *
     * ولو كان رئيسَ القسم نفسَه **ذهب تقريرُه إلى الإدارة مباشرة**. فتقريرُ
     * أداءٍ يصل عميلاً، **ومن يعتمد شغلَه بنفسه تصير المراجعةُ شكلاً** — وهي
     * قاعدةُ الفصل نفسُها التي منعت المنفّذَ من إجازة عنصر خطته.
     */
    "reports.review_head": {
        module: "reports",
        label: { ar: "مراجعة رئيس القسم", en: "Head review" },
        description: {
            ar: "المراجعة الأولى للتقرير — ولا يراجع أحدٌ ما كتبه هو.",
            en: "The first review of a report — nobody reviews what they wrote.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "reports.review_admin": {
        module: "reports",
        label: { ar: "مراجعة الإدارة والنشر", en: "Admin review and publish" },
        description: {
            ar: "المراجعة الثانية — وبها يصل التقرير إلى العميل في بوابته.",
            en: "The second review — this is what puts the report in front of the client.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "reports.exception.request": {
        module: "reports",
        label: { ar: "طلب استثناء تقرير", en: "Request a report exception" },
        description: {
            ar: "طلب رفع حدّ التقرير الشهري لعميل — لحملة خاصة أو تقرير تصحيحيّ.",
            en: "Ask to lift the monthly report limit for a client — for a campaign or a correction.",
        },
        allowedScopes: ["company", "department", "creator"],
        actionCapability: true,
    },
    /**
     * **خيطُ التقرير** (قرار شادي 2026-10-20: «التقارير بتاخد ملفات طبعاً
     * وتعليقات»).
     *
     * **وv3 نزل بلا خيطٍ على التقرير**، وv2 كان فيه `performance_report_comments`
     * — **فكان كلامُ المراجعة يُكتَب في الشات أو لا يُكتَب**. ومراجعةُ رئيسِ قسمٍ
     * ترجع بسببٍ في حقلٍ واحد، **والحوارُ الذي سبقها بلا موضع**.
     *
     * **وداخليٌّ بجمهوره**: العميلُ يطلب تعديلاً بـ`portal.reports.request_edit`
     * كما هو، ولا يرى خيطَ المراجعة.
     */
    "reports.comment": {
        module: "reports",
        label: { ar: "التعليق على تقرير", en: "Comment on a report" },
        description: {
            ar: "تعليق داخلي على التقرير وإشارة — للمراجعة بين فريق العمل، ولا يراه العميل. والمرفق مع التعليق.",
            en: "An internal comment and mentions on a report — for review between staff, never seen by the client. Files travel with the comment.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator"],
        actionCapability: true,
    },
    "reports.comments.moderate": {
        module: "reports",
        label: { ar: "إدارة تعليقات التقارير", en: "Moderate report comments" },
        description: {
            ar: "تعديل تعليق غيرك على تقرير أو حذفه — ومعه ملفاته.",
            en: "Edit or delete someone else's comment on a report — and its files with it.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **وملفُّ التقرير نفسِه — تسليمُه الذي يراه العميل.**
     *
     * **ولا تحمله `reports.update`**: فتلك «يعدّل ما لم يُعتمَد بعد»، **والملفُّ
     * يُرفَع بعد الاعتماد كذلك** — تقريرٌ منشورٌ يُلحَق به ملفٌّ طلبه العميل.
     */
    "reports.attach": {
        module: "reports",
        label: { ar: "ملفات التقرير", en: "Report files" },
        description: {
            ar: "ترفع ملفات التقرير وتفصلها — فهي تسليم العميل، ويراها في بوابة العميل.",
            en: "Upload and detach the report's own files — the client's deliverable, visible in their portal.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator"],
        actionCapability: true,
    },
    // ===== الوساطة والنشر =====
    /**
     * **والوساطةُ قسمٌ مركزيٌّ يخدم العملاء كلَّهم** — فنطاقُها الشركة.
     *
     * ونطاقُ «عملاء مسندون» كان سيعني إضافتَها إلى فريق كل عميل، **وعضويةُ
     * الفريق منحُ رؤية** (قرارٌ سابق في البلوبرنت): فتُمنَح لغرضٍ غير غرضها،
     * ويتوسّع ما تراه من بابٍ خلفيّ. والوساطةُ لا تُنتج أصلاً
     * (`planAssignable = false` على قسمها).
     */
    "moderation.queue.view": {
        module: "moderation",
        label: { ar: "طابور النشر", en: "The publishing queue" },
        description: {
            ar: "رؤية ما وافق عليه العميل وينتظر نشره، بمواعيده ومنصّاته.",
            en: "See what the client approved and is waiting to be published, with its dates and platforms.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    "moderation.schedule": {
        module: "moderation",
        label: { ar: "حجز موعد النشر", en: "Schedule a publication" },
        description: {
            ar: "تحديد موعد نشر صفّ على منصّة — ولا ينشر النظام بنفسه.",
            en: "Set when a row publishes on a platform — the system never publishes by itself.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **ولفريق الوساطة وحده** (قرار شادي) — لا لمن يراجع ولا لمن يكتب.
     *
     * فالوسمُ يقفل العنصر ويُظهره منتهياً عند العميل. **ولو وسمه غيرُ من نشره،
     * صار في السجلّ قولٌ لا صاحبَ له** — ولا يُعرَف من يُسأل.
     */
    "moderation.publish": {
        module: "moderation",
        label: { ar: "إثبات النشر", en: "Mark as published" },
        description: {
            ar: "وسم الصفّ منشوراً بإثباته ورابطه — ومنه يصير العنصر منتهياً عند العميل.",
            en: "Mark the row published with its proof and link — this is what makes the item done for the client.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **والسحبُ لرئيس قسم الوساطة وللمالك** (قرار شادي) — لا لمن وسم.
     *
     * فالسحبُ إقرارٌ بغلطٍ أو بإزالةٍ من المنصّة. **ولو سحبه الواسمُ نفسُه لمحا
     * أثرَ غلطته وحده** — وهو أوّلُ ما يُسأل عنه في نزاعٍ مع عميل. ويُفرَّق
     * «رئيسُ الوساطة» عن غيره **بدوره لا باسم قسمه في الكود**.
     */
    "moderation.unpublish": {
        module: "moderation",
        label: { ar: "سحب إثبات النشر", en: "Withdraw a publication proof" },
        description: {
            ar: "سحب وسم النشر بسبب — ويرجع العنصر إلى الجدولة ويُخطَر العميل.",
            en: "Withdraw the published mark with a reason — the item returns to scheduled and the client is told.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    /**
     * **وطلبُ الإزالة إجراءٌ حرجٌ لإنسان** (قرار شادي).
     *
     * فبوستٌ منشورٌ على منصّةٍ حقيقية **بايتاتٌ خارج النظام**، والنظامُ لا يدّعي
     * أنه ينزلها. ولا يُقفَل الطلبُ إلا بقول إنسانٍ إنه أنزله — **وقفلُه بضغطةٍ
     * بلا ذلك كذبٌ على العميل**.
     */
    "moderation.takedown.resolve": {
        module: "moderation",
        label: { ar: "إقفال طلب إزالة", en: "Close a takedown request" },
        description: {
            ar: "تأكيد أن المحتوى الحيّ أُنزل فعلاً من المنصّة — بيد إنسان، لا بوسم.",
            en: "Confirm that live content was actually taken down from the platform — by a human, not a flag.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    // ===== الإشعارات والإجراءات =====
    "notifications.view_own": {
        module: "notifications",
        label: { ar: "عرض إشعاراتي", en: "View my notifications" },
        description: {
            ar: "قائمة إشعاراتي وتعليمها كمقروءة.",
            en: "My notification list, and marking read.",
        },
        allowedScopes: ["own-record"],
        actionCapability: false,
    },
    "actions.view_own": {
        module: "notifications",
        label: { ar: "عرض إجراءاتي", en: "View my actions" },
        description: {
            ar: "القرارات المطلوبة منك في شاشة واحدة — في الفريق وبوابة العميل. ويختفي البند حين يُنجَز العمل.",
            en: "The decisions I owe, on one screen — staff and portal. An item clears when the work is done.",
        },
        allowedScopes: ["own-record"],
        actionCapability: false,
    },
    /**
     * **وتُسمّى بما تفعله** (قرار شادي ٢٠٢٦-١٠-١٩): **تأجيلٌ لا إنهاء.**
     *
     * وكانت `actions.complete_own` — «وسمُ بندٍ بأنه انتهى أو تأجيله». **وقرارُ
     * شادي ألغى الإنهاءَ اليدويّ**: فمن يقدر أن يُخفي قراراً مطلوباً منه بلا أن
     * يعمله **يجعل المركزَ يكذب**، وفي v2 كان مئةٌ وخمسةُ بنودٍ غلط ولم يستطع
     * أحدٌ أن يفرّق بين «اتعمل» و«اتخفى».
     *
     * **ووصفُ القدرة هو قائمةُ تسليمها** (درسُ شاشة الملفّ الشخصي): فلو بقي
     * الاسمُ «إنهاء» **لبقي زرٌّ موعودٌ لا وجود له**.
     */
    "actions.defer_own": {
        module: "notifications",
        label: { ar: "تأجيل إجراءاتي", en: "Defer my actions" },
        description: {
            ar: "«ذكّرني لاحقاً» على بند — فيعود في تاريخه. ولا يوجد زرّ «أنهيته»: يختفي البند حين يُنجَز العمل.",
            en: "Snooze an item until a date. There is no “done” button: an item clears when the work is done.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
    },
    // ===== طلبات الصلاحيات =====
    "access_requests.create": {
        module: "access_requests",
        label: { ar: "طلب صلاحية", en: "Request a permission" },
        description: {
            ar: "طلب قدرة بعينها بسبب مكتوب — بدل رسالة رفض جافة.",
            en: "Request a named capability with a written reason — instead of a bare refusal.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
    },
    "access_requests.resolve": {
        module: "access_requests",
        label: { ar: "البتّ في طلبات الصلاحيات", en: "Resolve permission requests" },
        description: {
            ar: "قبول الطلب فتُنشَأ المنحة فعلاً في المعاملة نفسها، أو رفضه بسبب.",
            en: "Approving actually creates the grant in the same transaction; or deny with a reason.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    // ===== المهام المباشرة — البلوبرنت §4.1 =====
    /**
     * **القراءة لا تُجزّأ.**
     *
     * من يرى المهمة يرى خيطها كاملاً — تعليقاتها ومرفقاتها. في v2 كانت
     * «رؤية التعليقات» قدرة منفصلة، فمن يملك الرؤية دونها يرى **لوحة تفشل دائماً**.
     */
    "tasks.view": {
        module: "tasks",
        label: { ar: "رؤية المهام المباشرة", en: "View direct tasks" },
        description: {
            ar: "رؤية المهمة وتعليقاتها ومرفقاتها معاً.",
            en: "See the task together with its comments and its attachments.",
        },
        allowedScopes: ["company", "department", "assignee", "creator"],
        actionCapability: false,
    },
    "tasks.create": {
        module: "tasks",
        label: { ar: "إنشاء مهمة وإسنادها", en: "Create and assign a task" },
        description: {
            ar: "إنشاء مهمة مباشرة واختيار مكلّفيها، وتغييرهم بعد ذلك.",
            en: "Create a direct task, choose who it is assigned to, and change that later.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "tasks.update": {
        module: "tasks",
        label: { ar: "تعديل مهمة", en: "Edit a task" },
        description: {
            ar: "تعديل العنوان والوصف والموعد والأولوية.",
            en: "Edit the title, the description, the due date, and the priority.",
        },
        allowedScopes: ["company", "department", "assignee", "creator"],
        actionCapability: true,
    },
    "tasks.change_state": {
        module: "tasks",
        label: { ar: "تحريك حالة مهمة", en: "Move a task's state" },
        description: {
            ar: "بدء العمل أو التعطيل بسبب أو الإنهاء. وإعادة الفتح تحتاج نطاق قسم أو شركة.",
            en: "Start, block with a reason, or finish. Reopening needs a department or company scope.",
        },
        allowedScopes: ["company", "department", "assignee"],
        actionCapability: true,
    },
    "tasks.comment": {
        module: "tasks",
        label: { ar: "التعليق على مهمة", en: "Comment on a task" },
        description: {
            ar: "كتابة تعليق والإشارة إلى زميل فيه.",
            en: "Write a comment and mention a colleague in it.",
        },
        allowedScopes: ["company", "department", "assignee", "creator"],
        actionCapability: true,
    },
    /**
     * **وإشرافُ تعليقات المهام — ولم تكن موجودة.**
     *
     * فتعليقُ المهمّة لم يكن يُحذَف من أحد، ولا من كاتبه. ولم يكن ذلك قراراً:
     * نسخةُ التعليقات الثانية نزلت أنحفَ من الأولى، **والفرقُ وُلد من النسخ**.
     * وكشفه توحيدُ العائلة، لا بلاغُ مستخدم.
     */
    "tasks.comments.moderate": {
        module: "tasks",
        label: { ar: "إدارة تعليقات المهام", en: "Moderate task comments" },
        description: {
            ar: "حذف تعليق غيره على مهمّة — والتعديل يبقى لصاحبه وحده.",
            en: "Delete someone else's comment on a task — editing stays with its author alone.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "tasks.attach": {
        module: "tasks",
        label: { ar: "إرفاق ملف بمهمة", en: "Attach a file to a task" },
        description: {
            ar: "رفع مرفق إلى المهمة، وحذف مرفقه هو.",
            en: "Upload an attachment to the task, and remove one's own.",
        },
        allowedScopes: ["company", "department", "assignee", "creator"],
        actionCapability: true,
    },
    // ===== الهوية والتنظيم — الشجرة الفرعية §4.2، معتمدة ٢٠٢٦-٠٩-٢٥ =====
    "identity.users.view": {
        module: "identity",
        label: { ar: "عرض الموظفين", en: "View employees" },
        description: {
            ar: "دليل الموظفين، وملفّ الشخص بأدواره وأقسامه وجلساته.",
            en: "The staff directory, and a person's profile with their roles, departments and sessions.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: false,
    },
    "identity.users.manage": {
        module: "identity",
        label: { ar: "إدارة الموظفين", en: "Manage employees" },
        description: {
            ar: "إضافة موظف، وتعديل بياناته، ونقله بين الأقسام، وإيقافه.",
            en: "Add an employee, edit their details, move them between departments, and deactivate them.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "identity.users.delete": {
        module: "identity",
        label: { ar: "حذف موظف", en: "Delete an employee" },
        description: {
            ar: "حذف موظف نهائياً. يبقى اسمه في السجلّ الجنائي وتاريخ العمل.",
            en: "Permanently delete an employee. Their name remains in the audit log and work history.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "identity.view_as": {
        module: "identity",
        label: { ar: "الدخول بعين موظف", en: "View as an employee" },
        description: {
            ar: "ترى النظام بصلاحيات موظفٍ حقيقيّة لتشخيص مشكلته، ولا يُكتب شيءٌ. ويُسجَّل باسمك، ولا تُفتح محادثاته الخاصة.",
            en: "See the system with an employee's real permissions to diagnose a problem; nothing is written. It is logged under your name, and their private chats stay closed.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "identity.security.manage": {
        module: "identity",
        label: { ar: "أمان حسابات الموظفين", en: "Employee account security" },
        description: {
            ar: "تصفير كلمة سرّ موظف، وإنهاء كل جلساته.",
            en: "Reset an employee's password and end all their sessions.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "roles.view": {
        module: "roles",
        label: { ar: "عرض الأدوار", en: "View roles" },
        description: {
            ar: "قائمة الأدوار ومصفوفة صلاحيات كلٍّ منها.",
            en: "The list of roles and each one's capability matrix.",
        },
        allowedScopes: ["company"],
        actionCapability: false,
    },
    "roles.manage": {
        module: "roles",
        label: { ar: "إدارة الأدوار ومصفوفتها", en: "Manage roles and their matrix" },
        description: {
            ar: "إنشاء دور، وتعديل مصفوفة صلاحياته، ونسخه، وحذفه.",
            en: "Create a role, edit its capability matrix, copy it, and delete it.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "roles.assign": {
        module: "roles",
        label: { ar: "إسناد دور لموظف", en: "Assign a role to an employee" },
        description: {
            ar: "إعطاء موظف دوراً — من الأدوار التي يحملها المُسنِد نفسه.",
            en: "Give an employee a role — one the assigner themselves holds.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "roles.override": {
        module: "roles",
        label: { ar: "استثناء فردي لموظف", en: "Individual override for an employee" },
        description: {
            ar: "منح موظف صلاحية خارج دوره، أو منعه منها — لمدّة محدودة بسبب مكتوب.",
            en: "Grant an employee a capability outside their role, or deny one — for a limited, justified period.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "org.departments.view": {
        module: "organization",
        label: { ar: "عرض الأقسام", en: "View departments" },
        description: {
            ar: "الأقسام ورؤساؤها وأعضاؤها وتفويضاتها.",
            en: "Departments with their heads, members and delegations.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: false,
    },
    "org.departments.manage": {
        module: "organization",
        label: { ar: "إدارة الأقسام", en: "Manage departments" },
        description: {
            ar: "إنشاء قسم، وتعديله، وتعيين رئيسه، وأرشفته.",
            en: "Create a department, edit it, set its head, and archive it.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "org.delegation.manage": {
        module: "organization",
        label: { ar: "تفويض سلطة رئيس قسم", en: "Delegate a department head's authority" },
        description: {
            ar: "تفويض سلطة رئيس القسم لشخصٍ من القسم لمدّة محدّدة.",
            en: "Delegate a department head's authority to someone in the department for a set period.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    // ===== العملاء =====
    "clients.view": {
        module: "clients",
        label: { ar: "عرض العملاء", en: "View clients" },
        description: {
            ar: "ملفّ العميل وفريقه وحدّ تخزينه المستهلَك.",
            en: "A client's profile, its team, and its consumed storage.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: false,
    },
    "clients.manage": {
        module: "clients",
        label: { ar: "إدارة العملاء", en: "Manage clients" },
        description: {
            ar: "إنشاء عميل وتعديل بياناته وتوقيته.",
            en: "Create a client and edit its details and timezone.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    /**
     * **إدارة فريق العميل — منحُ رؤية، لا تعديل بيانات.**
     *
     * نطاق «عملاء مسندون» يُشتقّ من هذه العضوية بعينها. فإدخال شخصٍ إلى فريق
     * عميل يوسّع ما يراه — وهو المسار **السادس** لسقف المنح، بعد الخمسة في §4.2.
     * وفي v2 كان الخطر معكوساً وأشدّ: صفرُ عضوياتٍ كان يعني «كل العملاء».
     */
    "clients.team.manage": {
        module: "clients",
        label: { ar: "إدارة فريق العميل", en: "Manage a client's team" },
        description: {
            ar: "إدخال موظف إلى فريق عميل وإخراجه، ووسم مدير الحساب.",
            en: "Add a member to a client's team or remove one, and mark the account manager.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "clients.archive": {
        module: "clients",
        label: { ar: "أرشفة العملاء", en: "Archive clients" },
        description: {
            ar: "أرشفة عميل — فيصير مقروءاً لا يُعدَّل — واستعادته.",
            en: "Archive a client — making it read-only — and restore it.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "clients.delete": {
        module: "clients",
        label: { ar: "حذف العملاء", en: "Delete clients" },
        description: {
            ar: "حذف عميل نهائياً — بعد أرشفته واكتمال تصديره.",
            en: "Permanently delete a client — after it is archived and its export has completed.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "clients.export": {
        module: "clients",
        label: { ar: "تصدير بيانات العميل", en: "Export a client's data" },
        description: {
            ar: "نسخة كاملة من عمل العميل وملفّاته — وهي شرطُ حذفه.",
            en: "A full copy of a client's work and files — and the precondition for deleting it.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "clients.storage.manage": {
        module: "clients",
        label: { ar: "إدارة حدّ التخزين", en: "Manage storage limits" },
        description: {
            ar: "رفع حدّ تخزين عميل أو خفضه، ومراجعة المستهلَك.",
            en: "Raise or lower a client's storage limit, and review what is consumed.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    // ===== البوردات =====
    "boards.view": {
        module: "boards",
        label: { ar: "عرض اللوحات", en: "View boards" },
        description: {
            ar: "لوحات العميل وأعمدتها وقوالبها.",
            en: "A client's boards, their columns, and the templates.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: false,
    },
    "boards.manage": {
        module: "boards",
        label: { ar: "إدارة اللوحات", en: "Manage boards" },
        description: {
            ar: "إنشاء لوحة، وأعمدتها، وترتيبها، وأرشفتها.",
            en: "Create a board, its columns, their order, and archive it.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    "boards.templates.manage": {
        module: "boards",
        label: { ar: "إدارة قوالب اللوحات", en: "Manage board templates" },
        description: {
            ar: "قوالب اللوحات وأعمدتها الافتراضية.",
            en: "Board templates and their default columns.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    // ===== الكروت =====
    "cards.view": {
        module: "cards",
        label: { ar: "عرض البطاقات", en: "View cards" },
        description: {
            ar: "بطاقات العميل وتفاصيلها وتعليقاتها ومرفقاتها وتاريخها.",
            en: "A client's cards with their details, comments, attachments, and history.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: false,
    },
    /**
     * **الإنشاء يحمل التكرار في نفس الطلب — ولا قدرة منفصلة له.**
     *
     * في v2 كان ضبط التكرار نداءً تالياً بقدرةٍ منفصلة، يُطلَق ولا تُفحَص نتيجته.
     * فالرفض هو الحالُ الافتراضي لمن يحمل الإنشاء ولا يحمل التكرار: الكارت يُنشأ
     * والتكرار يضيع بصمت، ولا يعلم أحدٌ إلى الأسبوع الذي لا يظهر فيه الكارت.
     */
    "cards.create": {
        module: "cards",
        label: { ar: "إنشاء البطاقات", en: "Create cards" },
        description: {
            ar: "إنشاء بطاقة بموعدها ومسنَديها وتكرارها في طلبٍ واحد.",
            en: "Create a card with its due date, assignees, and recurrence in one request.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    "cards.update": {
        module: "cards",
        label: { ar: "تعديل البطاقات", en: "Edit cards" },
        description: {
            ar: "محتوى البطاقة ومسنَدوها وتسمياتها وقائمة التحقّق فيها وموضعها بين الأعمدة.",
            en: "A card's content, assignees, labels, checklist, and its position between columns.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: true,
    },
    "cards.change_state": {
        module: "cards",
        label: { ar: "تحريك حالة البطاقة", en: "Change a card's state" },
        description: {
            ar: "نقل البطاقة في آلة حالتها — والتوقّف يستلزم سبباً.",
            en: "Move a card through its state machine — blocking requires a reason.",
        },
        allowedScopes: ["company", "department", "assigned-client", "assignee"],
        actionCapability: true,
    },
    "cards.comment": {
        module: "cards",
        label: { ar: "التعليق على البطاقات", en: "Comment on cards" },
        description: {
            ar: "كتابة تعليق والإشارة إلى من له علاقة بالبطاقة.",
            en: "Write a comment and mention people connected to the card.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: true,
    },
    /**
     * في v2 كانت نظيرتها **قابلة للمنح، مفروضة في الخلفية، ولا زرَّ لها في أي
     * مكان** — وتوأمها في الخطط له زرّ. قدرةٌ تُمنَح ولا تُستعمل.
     */
    "cards.comments.moderate": {
        module: "cards",
        label: { ar: "الإشراف على التعليقات", en: "Moderate comments" },
        description: {
            ar: "تعديل تعليق غيره أو حذفه — والتعديل يحفظ نسخته السابقة.",
            en: "Edit or delete someone else's comment — an edit keeps the previous revision.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "cards.attach": {
        module: "cards",
        label: { ar: "مرفقات البطاقات", en: "Card attachments" },
        description: {
            ar: "رفع مرفق على بطاقة وفصله — والفصل لا يهدم مرفق غيره.",
            en: "Attach a file to a card and detach it — detaching never destroys another card's copy.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: true,
    },
    "cards.labels.manage": {
        module: "cards",
        label: { ar: "إدارة تسميات العميل", en: "Manage a client's labels" },
        description: {
            ar: "قائمة تسميات العميل — إنشاء وتعديل وحذف. وإلصاقها ببطاقة تعديلٌ لها.",
            en: "A client's label catalogue — create, edit, delete. Attaching one to a card is a card edit.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    /**
     * **الأرشفة العامّة وحدها قدرة.** الأرشفة الشخصية جزءٌ من رؤية الشخص لشغله،
     * ولا قدرة لها — وفي v2 كانت قدرةً **غير مصنَّفة** في تصنيف نطاق القسم رغم
     * أنها تستعمل نفس سياق السجل، واختبار المعمارية الذي يمنع ذلك كان يفشل.
     */
    "cards.archive": {
        module: "cards",
        label: { ar: "أرشفة البطاقات", en: "Archive cards" },
        description: {
            ar: "أرشفة بطاقة عن الجميع واستعادتها — والاستعادة تعيد تكرارها.",
            en: "Archive a card for everyone and restore it — restoring reactivates its recurrence.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    "cards.due.request": {
        module: "cards",
        label: { ar: "طلب تغيير موعد", en: "Request a due-date change" },
        description: {
            ar: "طلب تغيير موعد بطاقة — طلبٌ واحدٌ معلَّق لكل بطاقة.",
            en: "Request a change to a card's due date — one pending request per card.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: true,
    },
    /**
     * **سلطةُ البتّ منفصلة عن سلطة الطلب** — قاعدة عمل ملزِمة في §4.
     * ويبتّ فيه رئيسُ القسم **أو مديرُ حساب العميل**، وأوّلُ من يبتّ يقفل
     * (قرار شادي ٢٠٢٦-٠٩-٢٦).
     */
    "cards.due.change": {
        module: "cards",
        label: { ar: "تغيير موعد البطاقة", en: "Change a card's due date" },
        description: {
            ar: "البتّ في طلب تغيير الموعد، وتغييره مباشرةً.",
            en: "Decide a due-change request, and change the date directly.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "cards.escalations.receive": {
        module: "cards",
        label: { ar: "استقبال تصعيد التأخير", en: "Receive lateness escalations" },
        description: {
            ar: "من يصله تصعيد البطاقة المتأخّرة بعد يومين، فوق رئيس قسم صاحبها.",
            en: "Who receives a late card's escalation after two days, beyond the owner's head.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    // ═══════════════════════ الخطط ودورة الاعتماد ═══════════════════════
    "plans.view": {
        module: "plans",
        label: { ar: "رؤية الخطط", en: "View plans" },
        description: {
            ar: "خطط العميل وعناصرها وتاريخها ووسائطها.",
            en: "A client's plans, their items, history and media.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: false,
    },
    "plans.manage": {
        module: "plans",
        label: { ar: "إدارة الخطط", en: "Manage plans" },
        description: {
            ar: "إنشاء خطة، وتعديل اسمها ومداها، وحذفها وهي فارغة.",
            en: "Create a plan, change its name and range, and delete it while empty.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "plans.items.create": {
        module: "plans",
        label: { ar: "إنشاء عنصر خطة", en: "Create a plan item" },
        description: {
            ar: "إنشاء عنصر بنوعه وأولويته وموعدَي تسليمه ونشره ومهامّه في نفس الطلب.",
            en: "Create an item with its type, priority, delivery and publish dates and its tasks in the same request.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    "plans.items.update": {
        module: "plans",
        label: { ar: "تعديل عنصر خطة", en: "Update a plan item" },
        description: {
            ar: "الموجز والمراجع ونصّ المنشور وTOV والمنصّات والأولوية وموعد النشر.",
            en: "Its brief, references, caption, TOV, platforms, priority and publish date.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: true,
    },
    "plans.items.assign": {
        module: "plans",
        label: { ar: "مهامّ العنصر", en: "Plan item tasks" },
        description: {
            ar: "إضافة مهمةٍ وحذفها، واختيار صاحبها وموعدها — ومكلَّفو العنصر هم أصحاب مهامّه.",
            en: "Add and remove a task, and choose its owner and due date — the item's assignees are its task owners.",
        },
        allowedScopes: ["company", "department", "assigned-client"],
        actionCapability: true,
    },
    /**
     * **نقلٌ لا إجازة.**
     *
     * وفي v2 كانت `plans.transition` قدرةً واحدة تحمل الاثنين — فالمنفّذ الذي
     * يحرّك عنصره من «مسنَد» إلى «جاري» **يعتمد بها عنصرَ نفسه داخلياً**. من يملك
     * تحريك شغله يملك إجازته.
     */
    "plans.items.transition": {
        module: "plans",
        label: { ar: "إتمام عنصرٍ منشور", en: "Close a published item" },
        description: {
            ar: "نقلُ العنصر المنشور إلى حالة الانتهاء — وما قبلها يُشتقّ من مهامّه.",
            en: "Move a published item to done — everything before that follows from its tasks.",
        },
        allowedScopes: ["company", "department", "assigned-client", "assignee"],
        actionCapability: true,
    },
    /**
     * **صاحبُ المهمة يشتغل عليها — وعلى مهمته وحدها.**
     *
     * و«المكلَّف» هنا يُقرأ **على المهمة نفسها** (صاحبُها)، لا على العنصر: فالمونتيرُ
     * لا يسلّم كفرَ زميله ولو كانا على عنصرٍ واحد. وهي القدرةُ الجديدة الوحيدة في
     * شجرة «مهامّ العنصر» (معتمَدة ٢٠٢٦-١٠-٢٨).
     */
    "plans.tasks.deliver": {
        module: "plans",
        label: { ar: "تسليم المهمة", en: "Deliver a task" },
        description: {
            ar: "بدء المهمة، ورفع معاينتها، ووضع رابط التسليم النهائيّ، وتسليمها للمراجعة.",
            en: "Start the task, upload its preview, set the final delivery link, and deliver it for review.",
        },
        allowedScopes: ["company", "department", "assignee"],
        actionCapability: true,
    },
    "plans.items.review": {
        module: "plans",
        label: { ar: "البتّ في المراجعة الداخلية", en: "Decide the internal review" },
        description: {
            ar: "اعتماد كلّ مهمةٍ وحدها أو إرجاعها بسبب — ويُعتمَد العنصر حين تُعتمَد كلُّ مهامّه، فيراه العميل.",
            en: "Approve each task on its own or return it with a reason — the item is approved once all its tasks are, and the client sees it.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "plans.items.cancel": {
        module: "plans",
        label: { ar: "إلغاء عنصر خطة", en: "Cancel a plan item" },
        description: {
            ar: "إلغاء عنصر بسببٍ إلزامي — ولا حذف، فالملغى يبقى شاهداً.",
            en: "Cancel an item with a mandatory reason — there is no delete; a cancelled item stays on record.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    /**
     * **تسجيلُ قرار العميل بالنيابة** — قرار شادي ٢٠٢٦-١٠-٠٨.
     *
     * وتبقى بعد نزول البورتال: عملاءُ يوافقون على الواتساب، وأحدٌ لا بدّ أن
     * يسجّلها. **و`onBehalf` عمودٌ صريح** لا سياقُ تدقيق — فأيُّ تقريرٍ يقرأ
     * الصفوف يعرف أيُّ موافقةٍ جاءت من العميل فعلاً.
     */
    "plans.items.decide_on_behalf": {
        module: "plans",
        label: { ar: "تسجيل قرار العميل بالنيابة", en: "Record a client decision on their behalf" },
        description: {
            ar: "تسجيل موافقة العميل أو رفضه — واختيار الجزء الذي يرجع — بسببٍ إلزامي، ويُميَّز في السجلّ عن نقرة العميل.",
            en: "Record the client's approval or rejection — choosing which part goes back — with a mandatory reason, distinguished in the record from the client's own click.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "plans.items.visibility": {
        module: "plans",
        label: { ar: "رؤية العميل للعنصر", en: "Client visibility of an item" },
        description: {
            ar: "إظهار العنصر للعميل وإخفاؤه يدوياً — والإظهار يشترط اعتماداً داخلياً.",
            en: "Show or hide the item from the client manually — showing requires internal approval.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "plans.items.withdraw": {
        module: "plans",
        label: { ar: "سحب الاعتماد", en: "Withdraw approval" },
        description: {
            ar: "إرجاع عنصرٍ معتمَد إلى التنفيذ، وإلغاء نشراته الحيّة.",
            en: "Return an approved item to production and cancel its live publications.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    /**
     * **وسائطُ العنصر الستّ بقدرةٍ واحدة** — بريف ومواد وتسليمات ونبرة صوت
     * ومعاينة وشرائح. وفي v2 كانت ستَّ مجموعاتٍ متوازية، وكلُّ واحدةٍ تنحرف.
     */
    "plans.media.manage": {
        module: "plans",
        label: { ar: "وسائط عنصر الخطة", en: "Plan item media" },
        description: {
            ar: "الموجز والمواد والمراجع والشرائح — ومعاينةُ المهمة وتسليمُها ملكُ «تسليم المهمة».",
            en: "Briefs, materials, references and carousel slides — a task's preview and delivery belong to «Deliver a task».",
        },
        allowedScopes: ["company", "department", "assigned-client", "assignee"],
        actionCapability: true,
    },
    "plans.comment": {
        module: "plans",
        label: { ar: "التعليق على عنصر", en: "Comment on an item" },
        description: {
            ar: "تعليق داخلي وإشارة — ولا يراه العميل.",
            en: "An internal comment and mentions — the client never sees these.",
        },
        allowedScopes: ["company", "department", "assigned-client", "creator", "assignee"],
        actionCapability: true,
    },
    "plans.comments.moderate": {
        module: "plans",
        label: { ar: "إدارة تعليقات الخطط", en: "Moderate plan comments" },
        description: {
            ar: "حذف تعليق غيره — والتعديل يبقى لصاحبه وحده.",
            en: "Delete someone else's comment — editing stays with its author alone.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "plans.revision_exceptions.resolve": {
        module: "plans",
        label: { ar: "البتّ في استثناء المراجعات", en: "Decide a revision exception" },
        description: {
            ar: "منح استثناءٍ لتجاوز حدّ مراجعات العميل أو رفضه.",
            en: "Grant or deny an exception to the client revision limit.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "plans.item_types.manage": {
        module: "plans",
        label: { ar: "أنواع العمل", en: "Work types" },
        description: {
            ar: "قائمة أنواع العمل وعلامتيها — يحتاج غلافاً، ومتعدّد الشرائح.",
            en: "The catalogue of work types and their two flags — needs a cover, and multi-slide.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    "plans.delete": {
        module: "plans",
        label: { ar: "حذف خطة فارغة", en: "Delete an empty plan" },
        description: {
            ar: "حذفٌ نهائي لخطةٍ بلا عناصر. وإدارةُ الخطط لا تشمله، فلا يقع بالغلط.",
            en: "Permanently delete a plan with no items. Managing plans does not include it, so it cannot happen by accident.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "files.remove_others": {
        module: "plans",
        label: { ar: "إزالة ملف رفعه غيرك", en: "Remove a file someone else uploaded" },
        description: {
            ar: "فصل ملفٍ عن كارت أو عنصر خطة وهو من رفع غيرك. وكلُّ موظفٍ يبدّل ملفَّه هو بلا هذه القدرة.",
            en: "Detach a file someone else uploaded from a card or plan item. Anyone can replace their own file without this.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: true,
    },
    "plans.overview": {
        module: "plans",
        label: { ar: "نظرة القسم على الخطط", en: "Cross-client plan overview" },
        description: {
            ar: "خطط كل العملاء في شاشةٍ واحدة — لرئيس القسم والإدارة.",
            en: "Every client's plans on one screen — for department heads and administration.",
        },
        allowedScopes: ["company", "department"],
        actionCapability: false,
    },
    /**
     * **فتحُ بورتال العميل وإدارةُ جهات اتصاله** — من ملفّ العميل.
     *
     * و§4.5: «جهةُ الاتصال حسابٌ كامل بدوره البورتاليّ، **يُنشئه مديرُ الحساب من
     * ملفّ العميل**». ولم يكن لذلك مسارٌ ولا شاشة، **فالبورتالُ كلُّه لم يكن
     * يُستعمَل في تركيبٍ حقيقي**: جهاتُ الاتصال الوحيدة كانت ما تزرعه البذرة.
     */
    "clients.portal.manage": {
        module: "clients",
        label: { ar: "بوابة العميل وجهات اتصاله", en: "The client portal and its contacts" },
        description: {
            ar: "فتح بوابة العميل وإغلاقه، وإضافة جهات اتصاله وإزالتها.",
            en: "Open or close a client's portal, and add or remove its contacts.",
        },
        allowedScopes: ["company", "assigned-client"],
        actionCapability: true,
    },
    "clients.publishing.manage": {
        module: "clients",
        label: { ar: "مسؤولية النشر", en: "Publishing responsibility" },
        description: {
            ar: "من ينشر عمل هذا العميل — الوكالة أم العميل بنفسه.",
            en: "Who publishes this client's work — the agency, or the client themselves.",
        },
        allowedScopes: ["company"],
        actionCapability: true,
    },
    // ═══════════════════════════ البورتال ═══════════════════════════
    /**
     * **قدراتُ جهة الاتصال — كلُّها `portalContact`، ونطاقُها واحد.**
     *
     * «عملاءُ مسندون» تُشتقّ من **عضوية البورتال** وحدها، ومن عميلٍ **بورتالُه
     * مفعَّل**. فإطفاءُ بورتال عميلٍ يسحب كلَّ قدرةٍ عليه في الحساب التالي — **لا
     * بزرٍّ ثانٍ يُنسى**.
     *
     * وستٌّ تنزل هنا، وخمسٌ معلَنةٌ في §4.5 بمراحلها: جدولُ النشر (السابعة)،
     * والمحادثةُ والاجتماعات (الثامنة)، والتقاريرُ وطلبُ تعديلها (التاسعة). ولا
     * تُسجَّل قبل موديولها — **فقدرةٌ تُمنَح ولا تفعل شيئاً هي صنف v2 بعينه**،
     * وبوابةُ «لكلّ قدرةٍ أثر» تكسر البناء عليها.
     */
    "portal.plans.view": {
        module: "portal",
        label: { ar: "خططي ومحتواها", en: "My plans and their content" },
        description: {
            ar: "خطط عملك وعناصرها المرئية لك — ما ينتظر قرارك وما اقترب موعد نشره.",
            en: "Your plans and the items visible to you — what awaits your decision and what publishes soon.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: false,
        portalContact: true,
    },
    /**
     * **وجدولُ النشر وإثباتُه** (البلوبرنت §4.5، السابعة).
     *
     * **ومُعلَنةٌ منذ المرحلة السابعة ولم تنزل**: شاشةُ الجدول كانت تحمل اسمَ
     * موديولٍ لا وجود له (`publishing` والحقيقيُّ `moderation`)، **فما طابقت
     * بوّابةُ «موديولٌ حيٌّ وشاشتُه غائبة» يوماً** — ونزلت المرحلةُ كاملةً
     * والعميلُ بلا جدولٍ يراه.
     *
     * **وموديولُها `moderation`** لا `portal`: فما تمسّه إثباتُ نشر.
     */
    "portal.schedule.view": {
        module: "moderation",
        label: { ar: "جدول النشر وإثباته", en: "The publishing schedule and its proof" },
        description: {
            ar: "مواعيد نشر عملك على كل منصّة، وحالة كل منها: بانتظار، مجدول، منشور — ورابط المنشور بعد نشره.",
            en: "When your work publishes on each platform and where each one stands: pending, scheduled, published — with the post's link once it is live.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: false,
        portalContact: true,
    },
    "portal.items.decide": {
        module: "portal",
        label: { ar: "الموافقة والرفض", en: "Approve and reject" },
        description: {
            ar: "الموافقة على عنصرٍ أو إرجاعُ جزءٍ منه — الريل أو الغلاف أو كلاهما — بتعليقٍ إلزاميّ يقول ماذا يُصلَح.",
            en: "Approve an item or send part of it back — the reel, the cover, or both — with a mandatory comment saying what to fix.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: true,
        portalContact: true,
    },
    "portal.items.request_edit": {
        module: "portal",
        label: { ar: "طلب تعديلٍ بعد الموافقة", en: "Request an edit after approving" },
        description: {
            ar: "طلب تعديلٍ على ما وافقتَ عليه — يُحسَب من مراجعاتك المتّفق عليها.",
            en: "Request an edit to something you approved — counted against your agreed revisions.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: true,
        portalContact: true,
    },
    /**
     * **ويطلب تعديلاً على تقريرٍ وصله** (البلوبرنت §4.5، التاسعة).
     *
     * **ولا يكتب هو التقرير ولا يُعيده مسودّةً**: يُسجَّل طلبُه بسببه، ويُبلَّغ
     * كاتبُه ورئيسُ قسمه، **ويبقى المنشورُ عنده كما هو** حتى تُعتمَد نسخةٌ جديدة.
     * فلو أعاده الطلبُ إلى المراجعة لاختفت أرقامٌ قرأها صاحبُها وبنى عليها.
     */
    "portal.reports.request_edit": {
        module: "reports",
        label: { ar: "طلب تعديلٍ على تقرير", en: "Request an edit to a report" },
        description: {
            ar: "طلب تصحيحٍ على تقريرٍ وصلك — يصل إلى كاتبه برسالتك، ويبقى التقرير لديك حتى تُعتمَد نسخة جديدة.",
            en: "Ask for a correction to a report you received — its author gets your note, and the report stays as it is until a new version is approved.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: true,
        portalContact: true,
    },
    /**
     * **ويكتب للوكالة** — قناةٌ واحدةٌ له، في مكانٍ واحد.
     *
     * **ولا يرى اسمَ موظّف**: الوكالةُ صوتٌ واحد. **وناسُه بأسمائهم** (قرارُ
     * المرحلة السادسة) — فيرى أن زميلتَه كتبت، ولا يرى من ردّ من عندنا.
     */
    "portal.chat.send": {
        module: "chat",
        label: { ar: "الكتابة للوكالة", en: "Write to the agency" },
        description: {
            ar: "تكتب لفريقك في الوكالة وتقرأ ردّهم، وترسل ملفات وتنزّل ما يرسلونه — كلامك وكلام زملائك بأسمائهم، والردّ باسم الوكالة.",
            en: "Write to your team at the agency, read their replies, and send and download files — you and your colleagues by name, the agency as one voice.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: true,
        portalContact: true,
    },
    /**
     * **ويطلب اجتماعاً ويردّ على اقتراحنا** — قدرةٌ واحدةٌ لطرفه كلِّه.
     *
     * **ولا يرى الرابطَ قبل التأكيد** (§3): فرابطٌ يُعرَض على موعدٍ مقترَحٍ يجعله
     * **يدخل غرفةً لا أحدَ فيها**. والحجبُ في القائمة البيضاء لا في الشاشة.
     */
    "portal.meetings.request": {
        module: "meetings",
        label: { ar: "طلب اجتماع", en: "Request a meeting" },
        description: {
            ar: "تطلب اجتماعاً مع فريقك في الوكالة، وتوافق على الموعد الذي يقترحونه أو تقترح غيره. ويظهر لك رابط الاجتماع بعد التأكيد.",
            en: "Ask your agency team for a meeting, and accept the time they propose or offer another. The meeting link appears once it is confirmed.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: true,
        portalContact: true,
    },
    "portal.items.comment": {
        module: "portal",
        label: { ar: "التعليق على عنصر", en: "Comment on an item" },
        description: {
            ar: "تعليقٌ على عنصر — يراه فريق الوكالة وزملاؤك في بوابة العميل.",
            en: "A comment on an item — the agency team and your portal colleagues both see it.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: true,
        portalContact: true,
    },
    "portal.contacts.view": {
        module: "portal",
        label: { ar: "زملائي في بوابة العميل", en: "My portal colleagues" },
        description: {
            ar: "من يشاركك بوابة العميل من موظفي شركتك — بأسمائهم، فتعرف مَن وافق ومَن رفض.",
            en: "Who shares the portal with you from your own company — by name, so you know who decided what.",
        },
        allowedScopes: ["assigned-client"],
        actionCapability: false,
        portalContact: true,
    },
    "portal.push.manage": {
        module: "portal",
        label: { ar: "تنبيه جهازي", en: "Notify this device" },
        description: {
            ar: "تثبيت التنبيه على هذا الجهاز وإلغاؤه — والاشتراكُ للجهاز لا للحساب.",
            en: "Turn notifications on or off for this device — a subscription belongs to a device, not an account.",
        },
        allowedScopes: ["own-record"],
        actionCapability: true,
        portalContact: true,
    },
};
export const CAPABILITY_KEYS = Object.keys(capabilities);
export function capabilityDefinition(key) {
    return capabilities[key];
}
/** قدرات التصرّف وحدها تصلح لجمهور إشعار. */
export function isActionCapability(key) {
    return capabilityDefinition(key).actionCapability;
}
//# sourceMappingURL=registry.js.map